import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { useBesluitId, usePatchBesluitId } from '@/api/queries/besluit';
import { useOpenModal } from '@/components/Modal';
import { SaveFailed } from '@/modals/SaveFailed';
import { ErrorMessage } from '@/utils/errorMessage';

import { ContentTemplate } from './-components/ContentTemplate/ContentTemplate';
import { AUTOSAVE_MUTATION_KEY } from './-components/Navigation/Navigation';
import { ToolbarContentPage } from './-components/ToolbarContentPage/ToolbarContentPage';

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/algemeen-toetsingskader'
)({
  loader: ({ params: { besluitId } }) =>
    useBesluitId.ensureQueryData(besluitId),
  component: function AlgemeenToetsingskader() {
    const { besluitId } = Route.useParams();

    const openModal = useOpenModal();

    const besluit = useBesluitId(besluitId);
    const patchBesluit = usePatchBesluitId({
      mutationKey: AUTOSAVE_MUTATION_KEY,
    });

    const onSave = useCallback(
      (value: string) => {
        if (besluit.data?.Toetsingskader === value) return;

        patchBesluit.mutate(
          {
            id: besluitId,
            data: {
              Toetsingskader: value,
            },
          },
          {
            onError: (error) => {
              const errorMessage = ErrorMessage.getLocalErrorMessage(error);

              if (errorMessage) {
                openModal(SaveFailed, {
                  tryAgain: () =>
                    patchBesluit.mutateAsync({
                      id: besluitId,
                      data: {
                        Toetsingskader: value,
                      },
                    }),
                });
              }
            },
          }
        );
      },
      [patchBesluit, besluitId, openModal, besluit.data?.Toetsingskader]
    );

    if (!besluit.data) {
      return null;
    }

    return (
      <>
        <ContentTemplate
          id="page-algemeen-toetsingskader-editor"
          title="Algemeen toetsingskader"
          defaultValue={besluit.data.Toetsingskader ?? ''}
          onSave={onSave}
        />

        <ToolbarContentPage besluit={besluit.data} />
      </>
    );
  },
});
